import { useState } from 'react';
import { AU_STATES, AU_TIMEZONES, type AuState, type SiteDTO, type TeamDTO } from '@em/shared';
import { api, qk, queryClient, useSites, useTeams, useUsers } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { titleCase } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

/* ---------------------------------------------------------------- sites */

type SiteForm = Omit<SiteDTO, 'id'> & { id?: string };
const blankSite = (): SiteForm => ({ name: '', kind: 'factory', state: 'NSW', timezone: AU_TIMEZONES.NSW, lat: -33.8688, lng: 151.2093, radiusM: 150, active: true });

export function SitesSettings() {
  const { can } = useAuth();
  const q = useSites();
  const [editing, setEditing] = useState<SiteForm | null>(null);
  return (
    <Card
      title="Sites and geofences"
      tight
      actions={
        can('owner') && (
          <Button variant="primary" size="sm" onClick={() => setEditing(blankSite())}>
            Add site
          </Button>
        )
      }
    >
      {!can('owner') && (
        <div className="card-body">
          <Alert tone="accent">Only the owner can add or change sites.</Alert>
        </div>
      )}
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No sites yet" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>State</th>
                  <th>Time zone</th>
                  <th>Location</th>
                  <th className="num">Radius</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id}>
                    <td className="strong">{s.name}</td>
                    <td>{titleCase(s.kind)}</td>
                    <td>{s.state}</td>
                    <td>{s.timezone}</td>
                    <td className="mono">
                      <a href={`https://www.openstreetmap.org/?mlat=${s.lat}&mlon=${s.lng}#map=17/${s.lat}/${s.lng}`} target="_blank" rel="noopener noreferrer">
                        {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                      </a>
                    </td>
                    <td className="num">{s.radiusM} m</td>
                    <td>{s.active ? <Badge tone="green">Active</Badge> : <Badge>Inactive</Badge>}</td>
                    <td className="actions">
                      {can('owner') && (
                        <Button size="sm" onClick={() => setEditing({ ...s })}>
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
      {editing && <SiteDialog initial={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function SiteDialog({ initial, onClose }: { initial: SiteForm; onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState(initial);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof SiteForm>(k: K, v: SiteForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id, ...b } = f;
      if (id) await api.updateSite(id, b);
      else await api.createSite(b);
      await queryClient.invalidateQueries({ queryKey: qk.sites });
      toast.success(id ? 'Site saved' : 'Site added');
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={f.id ? `Edit ${initial.name}` : 'Add site'}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <div className="form-grid">
          <Field label="Name" required className="full">
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} required />
          </Field>
          <Field label="Kind">
            <Select value={f.kind} onChange={(e) => set('kind', e.target.value as SiteDTO['kind'])}>
              {(['factory', 'install', 'supplier', 'other'] as const).map((k) => (
                <option key={k} value={k}>
                  {titleCase(k)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="State">
            <Select
              value={f.state}
              onChange={(e) => {
                const st = e.target.value as AuState;
                setF((x) => ({ ...x, state: st, timezone: AU_TIMEZONES[st] }));
              }}
            >
              {AU_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          </Field>
          <Field label="Time zone" hint="Set from the state; change only for a cross-border site">
            <Input value={f.timezone} onChange={(e) => set('timezone', e.target.value)} required />
          </Field>
          <Field label="Geofence radius (metres)" hint="20 to 5000">
            <Input type="number" min={20} max={5000} value={f.radiusM} onChange={(e) => set('radiusM', Number(e.target.value))} required />
          </Field>
          <Field label="Latitude" hint="Decimal degrees, negative in Australia">
            <Input type="number" step="any" min={-90} max={90} value={f.lat} onChange={(e) => set('lat', Number(e.target.value))} required />
          </Field>
          <Field label="Longitude">
            <Input type="number" step="any" min={-180} max={180} value={f.lng} onChange={(e) => set('lng', Number(e.target.value))} required />
          </Field>
          <div className="full small muted">Tip: in Google Maps, right-click the site entrance and copy the coordinates shown at the top of the menu.</div>
          <div className="full">
            <Checkbox label="Active (punches can match this site)" checked={f.active} onChange={(v) => set('active', v)} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- teams */

export function TeamsSettings() {
  const { can } = useAuth();
  const q = useTeams();
  const users = useUsers(can('owner'));
  const [editing, setEditing] = useState<Partial<TeamDTO> | null>(null);
  const userName = (id: string) => users.data?.find((u) => u.id === id)?.name;
  return (
    <Card
      title="Teams and supervisors"
      tight
      actions={
        <Button variant="primary" size="sm" onClick={() => setEditing({ name: '', supervisorUserIds: [] })}>
          Add team
        </Button>
      }
    >
      <p className="card-body small muted" style={{ margin: 0 }}>
        Supervisors see and approve the employees in their teams.
      </p>
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No teams yet" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th className="num">Active employees</th>
                  <th>Supervisors</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id}>
                    <td className="strong">{t.name}</td>
                    <td className="num">{t.employeeCount}</td>
                    <td>
                      {t.supervisorUserIds.length === 0 ? (
                        <span className="amber">None</span>
                      ) : users.data ? (
                        t.supervisorUserIds.map((id) => userName(id) ?? 'Unknown user').join(', ')
                      ) : (
                        `${t.supervisorUserIds.length} supervisor${t.supervisorUserIds.length === 1 ? '' : 's'}`
                      )}
                    </td>
                    <td className="actions">
                      <Button size="sm" onClick={() => setEditing(t)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
      {editing && <TeamDialog initial={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function TeamDialog({ initial, onClose }: { initial: Partial<TeamDTO>; onClose: () => void }) {
  const { can } = useAuth();
  const users = useUsers(can('owner'));
  const toast = useToast();
  const [name, setName] = useState(initial.name ?? '');
  const [sups, setSups] = useState<string[]>(initial.supervisorUserIds ?? []);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = can('owner') ? { name: name.trim(), supervisorUserIds: sups } : { name: name.trim() };
      if (initial.id) await api.updateTeam(initial.id, body);
      else await api.createTeam({ supervisorUserIds: [], ...body });
      await queryClient.invalidateQueries({ queryKey: qk.teams });
      await queryClient.invalidateQueries({ queryKey: qk.users });
      toast.success('Team saved');
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const candidates = users.data?.filter((u) => u.status === 'active' && u.roles.some((r) => r.role === 'supervisor')) ?? [];
  return (
    <Modal
      title={initial.id ? `Edit ${initial.name}` : 'Add team'}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!name.trim()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <Field label="Team name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <div className="field">
          <span className="label">Supervisors</span>
          {!can('owner') ? (
            <Alert tone="accent">Only the owner can see the list of portal users, so supervisors are assigned by the owner.</Alert>
          ) : candidates.length === 0 ? (
            <span className="muted">No users hold the supervisor role. Add one under Users and roles.</span>
          ) : (
            candidates.map((u) => (
              <Checkbox key={u.id} label={`${u.name} (${u.email})`} checked={sups.includes(u.id)} onChange={(on) => setSups((s) => (on ? [...s, u.id] : s.filter((x) => x !== u.id)))} />
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
